import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Lock, User, ArrowLeft, Sparkles, TrendingUp, Shield, Brain, Gift } from "lucide-react";

type AuthMode = "login" | "signup";

const showcaseItems = [
  {
    icon: Brain,
    question: "Comment Grafton peut piloter mon entreprise ?",
    answer: "Grafton analyse vos KPIs en temps réel, anticipe les tendances et vous propose des actions stratégiques pour garder une longueur d'avance.",
  },
  {
    icon: TrendingUp,
    question: "Peut-il vraiment booster mes ventes ?",
    answer: "Grafton identifie vos meilleures opportunités, optimise votre tunnel de vente et prédit votre chiffre d'affaires avec une précision redoutable.",
  },
  {
    icon: Shield,
    question: "Ma trésorerie est-elle en sécurité ?",
    answer: "Surveillance 24/7 de votre trésorerie, alertes intelligentes et recommandations stratégiques pour une gestion financière sans stress.",
  },
  {
    icon: Sparkles,
    question: "Que peut faire l'IA pour mon business ?",
    answer: "De la comptabilité automatisée aux campagnes marketing, Grafton gère l'opérationnel pour que vous vous concentriez sur la vision.",
  },
];

const Auth = () => {
  const [mode, setMode] = useState<AuthMode>("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [referralCode, setReferralCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [activeShowcase, setActiveShowcase] = useState(0);
  const [isTyping, setIsTyping] = useState(false);
  const [displayedAnswer, setDisplayedAnswer] = useState("");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, register, user } = useAuth();

  useEffect(() => {
    if (user) {
      // If user had triggered a subscription flow before logging in, resume it.
      let pendingPkg: string | null = null;
      try { pendingPkg = sessionStorage.getItem("pending_subscription"); } catch { /* ignore */ }
      if (pendingPkg) {
        try { sessionStorage.removeItem("pending_subscription"); } catch { /* ignore */ }
        navigate("/tarifs");
        return;
      }
      navigate("/dashboard");
    }
  }, [user, navigate]);

  // Capture ?ref=CODE from URL → prefill referral code and auto-switch to signup
  useEffect(() => {
    const ref = searchParams.get("ref");
    if (ref) {
      setReferralCode(ref.toUpperCase());
      setMode("signup");
    }
  }, [searchParams]);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveShowcase((prev) => (prev + 1) % showcaseItems.length);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    setDisplayedAnswer("");
    setIsTyping(true);
    const answer = showcaseItems[activeShowcase].answer;
    let i = 0;
    const typingInterval = setInterval(() => {
      if (i < answer.length) {
        setDisplayedAnswer(answer.slice(0, i + 1));
        i++;
      } else {
        setIsTyping(false);
        clearInterval(typingInterval);
      }
    }, 25);
    return () => clearInterval(typingInterval);
  }, [activeShowcase]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      navigate("/dashboard");
    } catch (err: any) {
      toast.error(err.message || "Identifiants incorrects");
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    // Client-side validation BEFORE hitting the backend
    if (!displayName.trim()) {
      toast.error("Veuillez saisir votre nom");
      return;
    }
    const cleanEmail = email.trim().toLowerCase();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i;
    if (!emailRegex.test(cleanEmail)) {
      toast.error("Adresse email invalide");
      return;
    }
    // Block reserved TLDs (RFC 2606)
    const reserved = /\.(test|example|invalid|localhost|local)$/i;
    if (reserved.test(cleanEmail)) {
      toast.error("Cette adresse email n'est pas valide. Utilisez un email réel (gmail.com, votre-domaine.fr…).");
      return;
    }
    if (password.length < 8) {
      toast.error("Le mot de passe doit contenir au moins 8 caractères");
      return;
    }
    setLoading(true);
    try {
      await register(cleanEmail, password, displayName.trim(), referralCode || undefined);
      if (referralCode) toast.success("Code de parrainage appliqué !");
      navigate("/dashboard");
    } catch (err: any) {
      console.error("[Signup error]", err, "message:", err?.message);
      toast.error(err.message || "Erreur lors de l'inscription");
    } finally {
      setLoading(false);
    }
  };

  const ActiveIcon = showcaseItems[activeShowcase].icon;

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left panel */}
      <div className="w-full lg:w-[480px] xl:w-[520px] flex flex-col justify-center p-8 lg:p-12 border-r border-border bg-card/50">
        <div>
          <div className="flex items-center gap-2 mb-16">
            <div className="h-9 w-9 rounded-lg bg-primary flex items-center justify-center">
              <span className="text-primary-foreground font-bold text-base">G</span>
            </div>
            <span className="font-semibold text-lg tracking-tight">Grafton IA</span>
          </div>

          <div className="max-w-sm">
            <h1 data-testid="auth-title" className="text-2xl font-bold mb-1">
              {mode === "login" ? "Bon retour" : "Créer un compte"}
            </h1>
            <p className="text-muted-foreground text-sm mb-8">
              {mode === "login"
                ? "Connectez-vous pour accéder à votre copilote IA."
                : "Commencez à piloter votre entreprise avec l'IA."}
            </p>

            <form onSubmit={mode === "login" ? handleLogin : handleSignup} data-testid="auth-form">
              <div className="space-y-4">
                {mode === "signup" && (
                  <div className="space-y-2">
                    <Label htmlFor="displayName">Nom</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="displayName"
                        data-testid="auth-name-input"
                        type="text"
                        placeholder="Votre nom"
                        value={displayName}
                        onChange={(e) => setDisplayName(e.target.value)}
                        className="pl-10"
                        required
                      />
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="email"
                      data-testid="auth-email-input"
                      type="email"
                      placeholder="vous@entreprise.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Mot de passe</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="password"
                      data-testid="auth-password-input"
                      type="password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10"
                      required
                      minLength={6}
                    />
                  </div>
                </div>

                {mode === "signup" && (
                  <div className="space-y-2">
                    <Label htmlFor="referralCode" className="flex items-center gap-1.5">
                      Code de parrainage <span className="text-xs text-muted-foreground font-normal">(optionnel — 1 mois gratuit)</span>
                    </Label>
                    <div className="relative">
                      <Gift className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-primary" />
                      <Input
                        id="referralCode"
                        data-testid="auth-referral-input"
                        type="text"
                        placeholder="Ex: GRAFTON7X"
                        value={referralCode}
                        onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                        className="pl-10 font-mono tracking-wider"
                        maxLength={12}
                      />
                    </div>
                  </div>
                )}

                <Button data-testid="auth-submit-btn" type="submit" variant="hero" className="w-full" size="lg" disabled={loading}>
                  {loading
                    ? "Chargement..."
                    : mode === "login"
                    ? "Se connecter"
                    : "S'inscrire"}
                </Button>
              </div>
            </form>

            <div className="mt-6 text-sm text-muted-foreground">
              {mode === "login" ? (
                <>
                  Pas encore de compte ?{" "}
                  <button data-testid="auth-switch-mode" onClick={() => setMode("signup")} className="text-primary hover:underline font-medium">
                    S'inscrire
                  </button>
                </>
              ) : (
                <button
                  data-testid="auth-switch-mode"
                  onClick={() => setMode("login")}
                  className="text-primary hover:underline font-medium inline-flex items-center gap-1"
                >
                  <ArrowLeft className="h-3 w-3" />
                  Retour à la connexion
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Right panel — Animated showcase */}
      <div className="hidden lg:flex flex-1 flex-col items-center justify-center p-12 xl:p-20 relative overflow-hidden bg-gradient-to-br from-background via-card to-background">
        <div className="relative z-10 max-w-lg w-full">
          <div className="mb-12 text-center">
            <h2 className="text-3xl xl:text-4xl font-bold mb-3">
              Votre <span className="text-gradient">copilote IA</span> stratégique
            </h2>
            <p className="text-muted-foreground text-lg">
              Grafton pense 200 coups d'avance pour votre entreprise.
            </p>
          </div>

          <div
            key={activeShowcase}
            className="rounded-2xl border border-border bg-card/80 backdrop-blur-sm p-8 shadow-[0_8px_40px_rgba(0,0,0,0.3)] animate-reveal-scale"
          >
            <div className="flex items-start gap-3 mb-6">
              <div className="h-10 w-10 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center flex-shrink-0">
                <ActiveIcon className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1 font-medium uppercase tracking-wider">Question</p>
                <p className="text-lg font-semibold leading-snug">
                  {showcaseItems[activeShowcase].question}
                </p>
              </div>
            </div>

            <div className="h-px bg-border mb-6" />

            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-xl bg-primary flex items-center justify-center flex-shrink-0">
                <span className="text-primary-foreground font-bold text-sm">G</span>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1 font-medium uppercase tracking-wider">Grafton</p>
                <p className="text-muted-foreground leading-relaxed">
                  {displayedAnswer}
                  {isTyping && <span className="inline-block w-0.5 h-4 bg-primary ml-0.5 animate-pulse align-middle" />}
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-center gap-2 mt-8">
            {showcaseItems.map((_, i) => (
              <button
                key={i}
                onClick={() => setActiveShowcase(i)}
                className={`h-2 rounded-full transition-all duration-300 ${
                  i === activeShowcase
                    ? "w-8 bg-primary"
                    : "w-2 bg-muted-foreground/30 hover:bg-muted-foreground/50"
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Auth;
